/**
 * GridCrash — Sound Engine (2.0-B3)
 *
 * All sounds are synthesized via Web Audio API.
 * No audio files, no loading, no network requests.
 *
 * Rules:
 * - AudioContext created lazily on first user gesture (browser requirement)
 * - Mute state persists in localStorage
 * - Respects prefers-reduced-motion (same audience tends to want quiet)
 * - All sounds are low-volume by design — this is a focus game, not an arcade
 */

const STORAGE_KEY = "gridcrash.sound.muted";

let ctx: AudioContext | null = null;
let muted = false;

// ─── Init ─────────────────────────────────────────────────────────────────────

/**
 * Call once on any user gesture to unlock the AudioContext.
 * Safe to call multiple times — no-ops after first call.
 */
export function initSound(): void {
  muted = localStorage.getItem(STORAGE_KEY) === "true";
  if (ctx) return;
  try {
    ctx = new AudioContext();
    // Some browsers start suspended — resume on gesture
    if (ctx.state === "suspended") ctx.resume();
  } catch {
    ctx = null;
  }
}

export function isMuted(): boolean {
  return muted;
}

export function toggleMute(): boolean {
  muted = !muted;
  try { localStorage.setItem(STORAGE_KEY, String(muted)); } catch { /* ignore */ }
  return muted;
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function getCtx(): AudioContext | null {
  if (muted || !ctx) return null;
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
}

function masterGain(ac: AudioContext, volume: number): GainNode {
  const g = ac.createGain();
  g.gain.value = volume;
  g.connect(ac.destination);
  return g;
}

// ─── Sounds ───────────────────────────────────────────────────────────────────

/**
 * Flag plant — a soft "thwick".
 * Filtered noise burst: white noise through a bandpass filter,
 * very fast attack and decay so it's crisp but not sharp.
 * Pitch-shifted slightly on each call so repeated flagging doesn't feel robotic.
 */
export function playFlag(): void {
  const ac = getCtx();
  if (!ac) return;

  const now    = ac.currentTime;
  const master = masterGain(ac, 0.18);

  // Noise source
  const bufferSize = ac.sampleRate * 0.08; // 80ms of noise
  const buffer     = ac.createBuffer(1, bufferSize, ac.sampleRate);
  const data       = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;

  const noise = ac.createBufferSource();
  noise.buffer = buffer;

  // Bandpass filter — center frequency varies slightly per call for variety
  const filter = ac.createBiquadFilter();
  filter.type            = "bandpass";
  filter.frequency.value = 800 + Math.random() * 400;
  filter.Q.value         = 3.5;

  // Envelope: very fast attack, short decay
  const env = ac.createGain();
  env.gain.setValueAtTime(0, now);
  env.gain.linearRampToValueAtTime(1, now + 0.005);
  env.gain.exponentialRampToValueAtTime(0.001, now + 0.07);

  noise.connect(filter);
  filter.connect(env);
  env.connect(master);

  noise.start(now);
  noise.stop(now + 0.08);
}

/**
 * Mine explosion — a low-frequency thud with rumble.
 * Two layers:
 *   1. A sine wave dropping quickly from ~80Hz to silence (the impact thud)
 *   2. A filtered noise burst with slow decay (the rumble)
 * Combined they feel like a dull underground detonation — present but not jarring.
 */
export function playExplosion(): void {
  const ac = getCtx();
  if (!ac) return;

  const now    = ac.currentTime;
  const master = masterGain(ac, 0.28);

  // Layer 1: pitched thud
  const osc = ac.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(90, now);
  osc.frequency.exponentialRampToValueAtTime(25, now + 0.35);

  const oscEnv = ac.createGain();
  oscEnv.gain.setValueAtTime(0.9, now);
  oscEnv.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

  osc.connect(oscEnv);
  oscEnv.connect(master);
  osc.start(now);
  osc.stop(now + 0.4);

  // Layer 2: low rumble noise
  const rumbleSize = ac.sampleRate * 0.6;
  const rumbleBuf  = ac.createBuffer(1, rumbleSize, ac.sampleRate);
  const rumbleData = rumbleBuf.getChannelData(0);
  for (let i = 0; i < rumbleSize; i++) rumbleData[i] = Math.random() * 2 - 1;

  const rumble = ac.createBufferSource();
  rumble.buffer = rumbleBuf;

  const rumbleFilter = ac.createBiquadFilter();
  rumbleFilter.type            = "lowpass";
  rumbleFilter.frequency.value = 120;
  rumbleFilter.Q.value         = 1.2;

  const rumbleEnv = ac.createGain();
  rumbleEnv.gain.setValueAtTime(0.6, now);
  rumbleEnv.gain.exponentialRampToValueAtTime(0.001, now + 0.55);

  rumble.connect(rumbleFilter);
  rumbleFilter.connect(rumbleEnv);
  rumbleEnv.connect(master);

  rumble.start(now);
  rumble.stop(now + 0.6);
}

/**
 * Fireworks sparkle — a sequence of ascending bright tones.
 * Called from launchFireworks() in main.ts, fires one musical note
 * per burst over the 10-second duration.
 *
 * Sound design: each "pop" is a short sine tone with a fast decay,
 * pitched to a pentatonic scale so multiple overlapping notes always
 * sound harmonious. Volume is gentle — this plays under the game modal.
 *
 * Returns a cleanup function that stops the sequence early (e.g. if
 * the player closes the modal or starts a new game).
 */
export function playFireworks(duration = 10000): () => void {
  const ac = getCtx();
  if (!ac) return () => {};

  // Pentatonic scale notes (Hz) — always sounds pleasant together
  const notes = [523, 659, 784, 1047, 1319, 1568, 2093];

  let running     = true;
  let noteIndex   = 0;
  const timeouts: ReturnType<typeof setTimeout>[] = [];

  function scheduleNote(): void {
    if (!running) return;

    const now    = ac!.currentTime;
    const master = masterGain(ac!, 0.09);

    // Each "pop" is two sine tones slightly detuned for shimmer
    [0, 7].forEach((detune, i) => {
      const osc     = ac!.createOscillator();
      const baseHz  = notes[noteIndex % notes.length];
      osc.type      = "sine";
      osc.frequency.value = baseHz + detune;

      const env = ac!.createGain();
      env.gain.setValueAtTime(0, now);
      env.gain.linearRampToValueAtTime(i === 0 ? 0.8 : 0.4, now + 0.01);
      env.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

      osc.connect(env);
      env.connect(master);
      osc.start(now);
      osc.stop(now + 0.36);
    });

    noteIndex++;

    // Schedule next note — slightly randomized interval so it feels alive
    const nextDelay = 600 + Math.random() * 400;
    const t = setTimeout(scheduleNote, nextDelay);
    timeouts.push(t);
  }

  // Fire immediately, then keep scheduling
  scheduleNote();

  // Auto-stop after duration
  const stopTimer = setTimeout(() => { running = false; }, duration);
  timeouts.push(stopTimer);

  return () => {
    running = false;
    timeouts.forEach(clearTimeout);
  };
}

/**
 * Aftershock tremor — a long sub-bass rumble, deeper and slower than the
 * explosion: the ground shifting, not a detonation. Pairs with a heavy
 * haptic in the UI layer.
 */
export function playTremor(): void {
  const ac = getCtx();
  if (!ac) return;

  const now    = ac.currentTime;
  const master = masterGain(ac, 0.24);

  // Layer 1: slow sub-bass sweep
  const osc = ac.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(55, now);
  osc.frequency.exponentialRampToValueAtTime(30, now + 0.9);

  const oscEnv = ac.createGain();
  oscEnv.gain.setValueAtTime(0, now);
  oscEnv.gain.linearRampToValueAtTime(0.8, now + 0.08);
  oscEnv.gain.exponentialRampToValueAtTime(0.001, now + 1.0);

  osc.connect(oscEnv);
  oscEnv.connect(master);
  osc.start(now);
  osc.stop(now + 1.0);

  // Layer 2: rolling low rumble
  const rumbleSize = ac.sampleRate * 1.1;
  const rumbleBuf  = ac.createBuffer(1, rumbleSize, ac.sampleRate);
  const rumbleData = rumbleBuf.getChannelData(0);
  for (let i = 0; i < rumbleSize; i++) rumbleData[i] = Math.random() * 2 - 1;

  const rumble = ac.createBufferSource();
  rumble.buffer = rumbleBuf;

  const rumbleFilter = ac.createBiquadFilter();
  rumbleFilter.type            = "lowpass";
  rumbleFilter.frequency.value = 90;
  rumbleFilter.Q.value         = 0.8;

  const rumbleEnv = ac.createGain();
  rumbleEnv.gain.setValueAtTime(0, now);
  rumbleEnv.gain.linearRampToValueAtTime(0.5, now + 0.15);
  rumbleEnv.gain.exponentialRampToValueAtTime(0.001, now + 1.05);

  rumble.connect(rumbleFilter);
  rumbleFilter.connect(rumbleEnv);
  rumbleEnv.connect(master);

  rumble.start(now);
  rumble.stop(now + 1.1);
}

/**
 * Hint chime — two soft ascending sine notes (E5 → B5), a gentle
 * "helpful" cue distinct from the flag thwick and cascade run.
 */
export function playHint(): void {
  const ac = getCtx();
  if (!ac) return;

  const master = masterGain(ac, 0.12);

  [659.25, 987.77].forEach((freq, i) => {
    const osc = ac.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;

    const env = ac.createGain();
    const start = ac.currentTime + i * 0.09;
    env.gain.setValueAtTime(0, start);
    env.gain.linearRampToValueAtTime(0.6, start + 0.015);
    env.gain.exponentialRampToValueAtTime(0.001, start + 0.25);

    osc.connect(env);
    env.connect(master);
    osc.start(start);
    osc.stop(start + 0.26);
  });
}

/**
 * Reveal sound — a soft tick for cascade reveals (the satisfying spread
 * of empty cells). Single cells on tap are silent to avoid noise on
 * every interaction.
 *
 * `step` walks an ascending pentatonic ladder so a long cascade plays
 * a climbing musical run — the bigger the cascade, the higher it goes.
 * Pentatonic notes always sound harmonious even when runs overlap.
 */
const CASCADE_SCALE = [523.25, 587.33, 659.25, 783.99, 880.0, 1046.5, 1174.7, 1318.5];

export function playCascade(step = 0): void {
  const ac = getCtx();
  if (!ac) return;

  const now    = ac.currentTime;
  const master = masterGain(ac, 0.06);

  const osc = ac.createOscillator();
  osc.type = "sine";
  osc.frequency.value = CASCADE_SCALE[Math.min(Math.max(step, 0), CASCADE_SCALE.length - 1)];

  const env = ac.createGain();
  env.gain.setValueAtTime(0.5, now);
  env.gain.exponentialRampToValueAtTime(0.001, now + 0.06);

  osc.connect(env);
  env.connect(master);
  osc.start(now);
  osc.stop(now + 0.06);
}
